<template>
  <PageContainer title="Criar Cobrança">
    <Box space="medium" class="container-box">
      <form @submit.prevent="handleSubmit">
        <Select :options="cfcs" legend="Autoescola" placeholder="Selecione uma autoescola" :modelValue="cfc"
          @update:modelValue="$event => (cfc = $event)" :disabled="loading" />

        <Textarea legend="Motivo da cobrança" @update:modelValue="$event => (justificativa = $event)"
          :modelValue="justificativa" :disabled="loading" />

        <SingleDate class="input-processo" legend="Data de vencimento" :modelValue="vencimento"
          @update:modelValue="$event => (vencimento = $event)" :minDate="minDate"  />

        <div class="input-container">
          <legend>Produtos/Serviços</legend>

          <Multiselect v-model="produtos" :options="produtosOptions" :multiple="true" :searchable="true"
            :close-on-select="false" :clear-on-select="false" :preserve-search="true" selectLabel="Selecionar"
            selectedLabel="Selecionado" deselectLabel="Remover" placeholder="Selecione as auto escolas" label="text"
            track-by="value" class="input input-full" />
        </div>

        <div class="resumo-cobranca" v-if="produtosSelecionados.length > 0">
          <h3>Resumo da Cobrança</h3>

          <div class="produtos-resumo">
            <div class="produto-item" v-for="produto in produtosSelecionados" :key="produto.id">
              <div class="produto-info">
                <span class="produto-nome">{{ produto.name }}</span>
                <span class="produto-preco">{{ utils.formatarValor(produto.value / 100) }}</span>
              </div>

              <div class="quantidade-controls">
                <button type="button" @click="diminuirQuantidade(produto.id)" :disabled="produto.quantidade <= 1"
                  class="btn-quantidade">-</button>
                <span class="quantidade">{{ produto.quantidade }}</span>
                <button type="button" @click="aumentarQuantidade(produto.id)" class="btn-quantidade">+</button>
              </div>

              <div class="produto-total">
                {{ utils.formatarValor((produto.value / 100) * produto.quantidade) }}
              </div>
            </div>
          </div>

          <div class="total-geral">
            <strong>Total: {{ utils.formatarValor(valorTotal) }}</strong>
          </div>
        </div>

        <div class="buttons">
          <Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger"
            :disabled="loading">
            <CloseCircleOutline :size="18" />
          </Button>

          <Button type="submit" text="Criar" color="primary" :disabled="loading">
            <Plus :size="18" />
          </Button>
        </div>
      </form>
    </Box>
  </PageContainer>
</template>

<script>
import PageContainer from 'core/PageContainer'
import Box from 'core/Box'
import Button from 'core/Button'
import LoadingSpinner from 'core/LoadingSpinner'
import Select from 'core/Select'
import Textarea from 'core/Textarea'
import SingleDate from 'core/SingleDate'

import CloseCircleOutline from 'icons/CloseCircleOutline'
import Plus from 'icons/Plus'

import utils from '../../services/utils'
import Multiselect from 'vue-multiselect'

export default {
  name: "NovaCobranca",
  components: {
    PageContainer,
    Box,
    Button,
    LoadingSpinner,
    Select,
    Textarea,
    SingleDate,
    Multiselect,

    CloseCircleOutline,
    Plus,
  },
  data() {
    return {
      utils: utils,
      cfc: null,
      justificativa: '',
      vencimento: '',
      cfcs: [],
      produtos: [],
      produtosOptions: [],
      produtosCompletos: [],
      loading: false,
    }
  },
  computed: {
    minDate() {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow.toISOString().split('T')[0]
    },
    produtosSelecionados() {
      return this.produtos.map(produtoSelecionado => {
        const produtoCompleto = this.produtosCompletos.find(p => p.id === produtoSelecionado.value)
        return {
          ...produtoCompleto,
          quantidade: produtoSelecionado.quantidade || 1
        }
      }).filter(p => p.id)
    },
    valorTotal() {
      return this.produtosSelecionados.reduce((total, produto) => {
        return total + ((produto.value / 100) * produto.quantidade)
      }, 0)
    }
  },
  async created() {
    await Promise.all([
      this.fetchCfcs(),
      this.fetchProdutos(),
    ])
  },
  methods: {
    async fetchCfcs() {
      this.loadingCfc = true

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola`)
        .then(response => {
          this.cfcs = response.body.dados.map(cfc => ({ value: cfc.id_cfc, text: cfc.nome_fantasia }))
        })
        .catch(err => {
          this.$toasted.show('Erro ao buscar autoescolas.', {
            icon: 'fa-times',
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loadingCfc = false
        })
    },
    async fetchProdutos() {
      this.loading = true

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/produtos/fetch`)
        .then(response => {
          this.produtosCompletos = response.body.dados
          this.produtosOptions = response.body.dados.map(produto => ({ value: produto.id, text: `${produto.name} - ${utils.formatarValor(produto.value / 100)}` }))
        })
        .catch(error => {
          this.$toasted.show('Erro ao buscar produtos/serviços.', {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async handleSubmit() {
      this.loading = true

      if (!this.cfc) {
        this.$toasted.show('Selecione uma autoescola.', {
          position: 'top-right',
        })

        this.loading = false
        return
      }

      if (!this.justificativa) {
        this.$toasted.show('Digite uma justificativa.', {
          position: 'top-right',
        })

        this.loading = false
        return
      }

      if (!this.vencimento) {
        this.$toasted.show('Digite uma data de vencimento.', {
          position: 'top-right',
        })

        this.loading = false
        return
      }

      const today = new Date()
      const selectedDate = new Date(this.vencimento)
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      
      if (selectedDate < tomorrow) {
        this.$toasted.show('A data de vencimento deve ser pelo menos 1 dia a mais que hoje.', {
          position: 'top-right',
        })

        this.loading = false
        return
      }

      if (this.produtosSelecionados.length === 0) {
        this.$toasted.show('Selecione pelo menos um produto.', {
          position: 'top-right',
        })

        this.loading = false
        return
      }

      const data = {
        id_cfc: this.cfc,
        reason: this.justificativa,
        due_date: this.vencimento,
        products: this.produtosSelecionados.map(produto => ({
          id: produto.id,
          quantity: produto.quantidade,
        })),
      }

      await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/cobrancas`, data)
        .then(response => {
          this.clearInputs()

          this.$toasted.show('Cobrança criada com sucesso.', {
            position: 'top-right',
          })
        })
        .catch(error => {
          this.$toasted.show('Erro ao criar cobrança.', {
            position: 'top-right',
          })
        })

      this.loading = false
    },
    aumentarQuantidade(produtoId) {
      const produto = this.produtos.find(p => p.value === produtoId)
      if (produto) {
        produto.quantidade = (produto.quantidade || 1) + 1
      }
    },
    diminuirQuantidade(produtoId) {
      const produto = this.produtos.find(p => p.value === produtoId)
      if (produto && produto.quantidade > 1) {
        produto.quantidade = produto.quantidade - 1
      }
    },
    clearInputs() {
      this.cfc = null
      this.justificativa = ''
      this.vencimento = ''
      this.produtos = []
    }
  },
  watch: {
    produtos: {
      handler(newProdutos) {
        newProdutos.forEach(produto => {
          if (!produto.quantidade) {
            this.$set(produto, 'quantidade', 1)
          }
        })
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
form {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  .buttons {
    align-self: flex-end;

    display: flex;
    gap: 0.5rem;
  }
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: space-between;

  legend {
    margin: 0;
  }
}

.resumo-cobranca {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;

  h3 {
    margin: 0 0 1rem 0;
    color: #1C2D5A;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .produtos-resumo {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;

    .produto-item {
      display: grid;
      grid-template-columns: 1fr auto auto;
      align-items: center;
      gap: 1rem;
      padding: 1rem;
      background: white;
      border: 1px solid #dee2e6;
      border-radius: 6px;

      .produto-info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .produto-nome {
          font-weight: 500;
          color: #333;
        }

        .produto-preco {
          font-size: 0.875rem;
          color: #6c757d;
        }
      }

      .quantidade-controls {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .btn-quantidade {
          width: 32px;
          height: 32px;
          border: 1px solid #dee2e6;
          background: white;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;

          &:hover:not(:disabled) {
            background: #e9ecef;
            border-color: #adb5bd;
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        }

        .quantidade {
          min-width: 2rem;
          text-align: center;
          font-weight: 500;
        }
      }

      .produto-total {
        font-weight: 600;
        color: #1C2D5A;
        text-align: right;
      }
    }
  }

  .total-geral {
    padding-top: 1rem;
    border-top: 2px solid #dee2e6;
    text-align: right;
    font-size: 1.125rem;
    color: #1C2D5A;
  }
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: space-between;

  legend {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    line-height: 100%;
    color: #606264;
  }
}
</style>